import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CircleDollarSign,
  Hash,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
} from 'lucide-react';
import {
  api,
  ApiError,
  type SmsCandidateServer,
  type SmsNumber,
  type SmsNumbers as SmsNumbersData,
  type SmsNumberStatus,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { Drawer } from '../components/ui/Drawer';
import { Fact, FactSection } from '../components/ui/Facts';
import { ServerCell, ServerFacts } from '../components/ServerFacts';
import { SmsAcquireDrawer } from '../components/SmsAcquireDrawer';
import {
  SmsAssignmentEditor,
  type AssignmentTarget,
} from '../components/SmsAssignmentEditor';
import { useSort } from '../lib/sort';
import { formatDateTime, formatNumber, formatPhone, formatUsd } from '../lib/format';
import { useAuth } from '../contexts/use-auth';
import { cn } from '../lib/utils';

type StatusFilter = 'all' | 'assigned' | 'spare' | 'problems';
const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'spare', label: 'Spare' },
  { value: 'problems', label: 'Needs attention' },
];

const STATUS_LABEL: Record<SmsNumberStatus, string> = {
  assigned: 'Assigned',
  shared: 'Shared',
  conflicted: 'Server conflict',
  duplicate: 'Duplicate',
  spare: 'Spare',
  default: 'Fallback',
  unleased: 'Not leased',
};

// A row's primary customer label, for searching and sorting. Multi-server
// numbers sort under whichever server comes first, which is also what shows.
const customerOf = (n: SmsNumber) => n.assignments[0]?.customer ?? '';

const getValue = (item: SmsNumber, key: string): unknown => {
  switch (key) {
    case 'phoneNumber':
      return item.phoneNumber;
    case 'customer':
      return customerOf(item).toLowerCase();
    case 'server': {
      const first = item.assignments[0];
      if (first?.server?.name) return first.server.name.toLowerCase();
      // Rows with no instance sort last, and dotted quads sort as text
      // otherwise — .100 would land before .23.
      const padded = (first?.serverIp ?? '')
        .split('.')
        .map((octet) => octet.padStart(3, '0'))
        .join('.');
      return `￿${padded}`;
    }
    case 'status':
      return item.status;
    case 'createdAt':
      return item.lease?.createdAt ?? '';
    default:
      return undefined;
  }
};

export function SmsNumbers() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canWrite = can('sms.write');
  const canAcquire = can('sms.acquire');

  const numbers = useQuery({
    queryKey: ['sms', 'numbers'],
    queryFn: () => api.listSmsNumbers(),
  });

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [detail, setDetail] = useState<SmsNumber | null>(null);
  const [acquiring, setAcquiring] = useState(false);

  const items = useMemo(() => numbers.data?.items ?? [], [numbers.data]);
  const summary = numbers.data?.summary;
  const problems = useMemo(() => items.filter((n) => n.problem), [items]);
  const quota = numbers.data?.capacity?.phoneNumbers ?? null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((n) => {
      if (filter === 'problems' && !n.problem) return false;
      if (filter === 'spare' && n.status !== 'spare') return false;
      if (filter === 'assigned' && n.assignments.length === 0) return false;
      if (!needle) return true;
      return (
        n.phoneNumber.toLowerCase().includes(needle) ||
        formatPhone(n.phoneNumber).toLowerCase().includes(needle) ||
        n.assignments.some(
          (a) =>
            a.customer.toLowerCase().includes(needle) ||
            a.serverIp.toLowerCase().includes(needle) ||
            (a.organizationName ?? '').toLowerCase().includes(needle) ||
            (a.server?.name ?? '').toLowerCase().includes(needle) ||
            (a.server?.instanceId ?? '').toLowerCase().includes(needle),
        )
      );
    });
  }, [items, q, filter]);

  const { sortedItems, sort, toggle } = useSort(filtered, getValue, {
    key: 'status',
    direction: 'asc',
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">SMS Numbers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every leased 10DLC number, the X5 server and customer it sends for, and where the
            registry and AWS disagree.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {numbers.data?.asOf && (
            <span className="text-xs text-muted-foreground">
              as of {formatDateTime(numbers.data.asOf)}
            </span>
          )}
          {canAcquire && (
            <Button variant="outline" onClick={() => setAcquiring(true)}>
              <Plus className="h-4 w-4" />
              Acquire
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => void numbers.refetch()}
            disabled={numbers.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', numbers.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {numbers.error && <p className="text-sm text-destructive">{(numbers.error as Error).message}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Leased numbers" icon={<Hash className="h-4 w-4 text-muted-foreground" />}>
          <div className="text-2xl font-bold tabular-nums">{formatNumber(summary?.leased)}</div>
          <p className="text-xs text-muted-foreground">
            {formatUsd(summary?.monthlyCost)} per month
          </p>
        </StatCard>
        <StatCard title="Assigned" icon={<Server className="h-4 w-4 text-muted-foreground" />}>
          <div className="text-2xl font-bold tabular-nums">{formatNumber(summary?.assigned)}</div>
          <p className="text-xs text-muted-foreground">
            {formatNumber(summary?.servers)} servers · {formatNumber(summary?.customers)} customers
          </p>
          {summary && summary.multiNumberServers > 0 && (
            <p className="mt-0.5 text-xs text-destructive">
              {formatNumber(summary.multiNumberServers)} with more than one number
            </p>
          )}
          {summary && (summary.stoppedServers > 0 || summary.unresolvedServers > 0) && (
            <p className="mt-0.5 text-xs text-caution">
              {[
                summary.stoppedServers > 0 && `${formatNumber(summary.stoppedServers)} stopped`,
                summary.unresolvedServers > 0 &&
                  `${formatNumber(summary.unresolvedServers)} with no instance`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </StatCard>
        <StatCard
          title="Spare"
          icon={<CircleDollarSign className="h-4 w-4 text-muted-foreground" />}
        >
          <div className="text-2xl font-bold tabular-nums">{formatNumber(summary?.spare)}</div>
          <p className="text-xs text-muted-foreground">
            {formatUsd(summary?.spareMonthlyCost)} per month idle
          </p>
          {quota && (
            <p
              className={cn(
                'mt-0.5 text-xs',
                quota.used >= quota.max ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {quota.used >= quota.max
                ? `quota full — ${formatNumber(quota.used)}/${formatNumber(quota.max)} leased`
                : `${formatNumber(quota.max - quota.used)} more leasable`}
            </p>
          )}
        </StatCard>
        <StatCard
          title="Needs attention"
          icon={
            <AlertTriangle
              className={cn(
                'h-4 w-4',
                summary && summary.problems > 0 ? 'text-destructive' : 'text-muted-foreground',
              )}
            />
          }
        >
          <div
            className={cn(
              'text-2xl font-bold tabular-nums',
              summary && summary.problems > 0 && 'text-destructive',
            )}
          >
            {formatNumber(summary?.problems)}
          </div>
          <p className="text-xs text-muted-foreground">
            {summary?.problems === 0 ? 'registry matches AWS' : 'registry and AWS disagree'}
          </p>
        </StatCard>
      </div>

      {problems.length > 0 && <ProblemsCard problems={problems} />}

      {numbers.data?.truncated && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Part of the inventory could not be read, so this list may be incomplete and a server
          shown without an instance may be a gap rather than a stale row.
        </p>
      )}
      {numbers.data?.directoryTruncated && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          The X5 organization roster was too large to read in one pass, so some customer labels may
          show without an organization link.
        </p>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search number, customer, server IP…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  filter === f.value
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-xs text-muted-foreground">
            {formatNumber(sortedItems.length)} of {formatNumber(items.length)}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <SortableHeader label="Number" sortKey="phoneNumber" sort={sort} onToggle={toggle} />
                <SortableHeader label="Customer" sortKey="customer" sort={sort} onToggle={toggle} />
                <SortableHeader label="X5 server" sortKey="server" sort={sort} onToggle={toggle} />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Leased since"
                  sortKey="createdAt"
                  sort={sort}
                  onToggle={toggle}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {numbers.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((n) => (
                <tr
                  key={n.phoneNumber}
                  className={cn(
                    'border-b border-border transition-colors last:border-0 hover:bg-muted/50',
                    n.problem && 'bg-destructive/5 shadow-[inset_2px_0_0_var(--color-destructive)]',
                  )}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setDetail(n)}
                      className="group block cursor-pointer text-left font-mono"
                      title="Show number detail"
                    >
                      <span className="group-hover:text-accent group-hover:underline">
                        {formatPhone(n.phoneNumber)}
                      </span>
                      {n.lease?.twoWayEnabled === false && (
                        <div className="font-sans text-xs text-muted-foreground">one-way</div>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <CustomerCell number={n} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {n.assignments.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      n.assignments.map((a) => (
                        <ServerCell
                          key={a.serverIp}
                          server={a.server}
                          address={a.serverIp}
                          onOpen={() => setDetail(n)}
                        />
                      ))
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={n.status} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-muted-foreground">
                    {n.lease?.createdAt ? formatDateTime(n.lease.createdAt) : '—'}
                  </td>
                </tr>
              ))}
              {!numbers.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    {items.length === 0 ? 'No numbers found.' : 'No numbers match that filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {acquiring && (
        <SmsAcquireDrawer
          capacity={numbers.data?.capacity}
          onClose={() => setAcquiring(false)}
          onAcquired={() => void queryClient.invalidateQueries({ queryKey: ['sms', 'numbers'] })}
        />
      )}

      <NumberDrawer
        number={detail}
        candidates={numbers.data?.candidates}
        canWrite={canWrite}
        onClose={() => setDetail(null)}
        onChanged={() => void queryClient.invalidateQueries({ queryKey: ['sms', 'numbers'] })}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4 text-muted-foreground" />
            Reading this page
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Two independent records are compared here. AWS End User Messaging knows what we{' '}
            <span className="text-foreground">lease and pay for</span>;{' '}
            <code className="font-mono text-xs">{numbers.data?.registryTable ?? 'the registry'}</code>{' '}
            keyed by X5 server IP is what the SMS gateway{' '}
            <span className="text-foreground">actually sends from</span>. Neither is derived from the
            other, so they can drift.
          </p>
          <p>
            A server with no registry row falls back to the shared number{' '}
            <span className="font-mono text-foreground">
              {formatPhone(numbers.data?.defaultOriginationNumber)}
            </span>
            . That is also what happens when a row names a number we do not lease — which is why{' '}
            <span className="text-foreground">Not leased</span> matters: those customers look
            configured but are sending from the shared number.
          </p>
          <p>
            A registry row identifies its server by private IP, because that is all the SMS gateway
            sees on an inbound request. The instance name and id come from resolving that address
            against EC2, so a row showing{' '}
            <span className="text-foreground">no instance</span> is one whose server no longer
            exists — and a <span className="text-foreground">stopped</span> one is a number being
            billed for a server that cannot send.
          </p>
          <p>
            <span className="text-foreground">Spare</span> numbers are leased and billed with nothing
            sending from them. That is expected headroom for onboarding, not a fault — the cost above
            is just so the size of it is visible.
          </p>
          <p>
            A customer is linked to an X5 organization only when the registry label matches an
            organization name exactly. The labels were typed by hand over four years, so most stay
            plain text rather than being guessed onto the nearest match.
          </p>
          <p>
            This page is read-only. Assigning, reassigning, and releasing numbers still happen
            outside the portal.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerCell({ number }: { number: SmsNumber }) {
  if (number.assignments.length === 0) {
    return (
      <span className="text-muted-foreground italic">
        {number.status === 'default' ? 'Shared fallback' : 'Unassigned'}
      </span>
    );
  }
  return (
    <div className="space-y-0.5">
      {number.assignments.map((a) => (
        <div key={a.serverIp} className="flex items-center gap-1.5">
          {a.organizationId ? (
            <Link
              to={`/organizations/${encodeURIComponent(a.organizationId)}`}
              className="text-accent hover:underline"
            >
              {a.organizationName}
            </Link>
          ) : (
            <span className="text-fg-2">{a.customer || '—'}</span>
          )}
          {a.organizationId && a.organizationName !== a.customer && (
            <span className="font-mono text-xs text-muted-foreground/70">{a.customer}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The row has more to say than five columns can hold: everything
 * DescribePhoneNumbers returns about the lease, and everything the private-IP
 * lookup found about the server. Instance tags are shown wholesale rather than
 * curated — `TeleportHostname` is how you reach the box and `DataExportName` is
 * its datalake client id, and a tag added later should appear without a code
 * change here.
 */
function NumberDrawer({
  number,
  candidates,
  canWrite,
  onClose,
  onChanged,
}: {
  number: SmsNumber | null;
  candidates: SmsNumbersData['candidates'] | undefined;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<AssignmentTarget | null>(null);
  const [removing, setRemoving] = useState(false);
  const [togglingTwoWay, setTogglingTwoWay] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Any change invalidates the candidate lists this drawer was opened with, so
  // it closes rather than staying open over data it can no longer trust.
  const saved = () => {
    setEditing(null);
    onChanged();
    onClose();
  };

  const close = () => {
    setEditing(null);
    setError(null);
    onClose();
  };

  const toggleTwoWay = async (n: SmsNumber) => {
    const enabling = n.lease?.twoWayEnabled === false;
    const who = n.assignments.map((a) => a.customer).join(', ') || 'nobody';
    if (
      !confirm(
        enabling
          ? [
              `Enable two-way messaging on ${formatPhone(n.phoneNumber)}?`,
              '',
              'Replies to this number will start reaching the SMS gateway instead of',
              'being dropped by AWS.',
            ].join('\n')
          : [
              `Disable two-way messaging on ${formatPhone(n.phoneNumber)}?`,
              '',
              `Assigned to: ${who}`,
              '',
              'Anything a recipient texts back to this number will be dropped by AWS',
              'before it reaches the gateway — silently, with no delivery failure',
              'recorded anywhere. Outbound alarms are unaffected.',
            ].join('\n'),
      )
    ) {
      return;
    }
    setTogglingTwoWay(true);
    setError(null);
    try {
      await api.setSmsTwoWay(n.phoneNumber, enabling);
      saved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setTogglingTwoWay(false);
    }
  };

  const remove = async (serverIp: string, phoneNumber: string, label: string) => {
    if (
      !confirm(
        [
          `Remove ${formatPhone(phoneNumber)} from ${label}?`,
          '',
          'That server stops sending from its own number and falls back to the shared',
          'origination number. Its alarms keep working, but they no longer come from a',
          'number the customer recognises.',
        ].join('\n'),
      )
    ) {
      return;
    }
    setRemoving(true);
    setError(null);
    try {
      await api.deleteSmsAssignment(serverIp, phoneNumber);
      saved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  const assignable = canWrite && candidates !== undefined;

  return (
    <Drawer
      open={number !== null}
      onClose={close}
      title={
        number && (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold">
                {formatPhone(number.phoneNumber)}
              </span>
              <StatusBadge status={number.status} />
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {number.assignments.map((a) => a.customer).join(' · ') || 'Unassigned'}
            </p>
          </div>
        )
      }
    >
      {number && (
        <div className="space-y-6">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {editing && candidates && (
            <SmsAssignmentEditor
              target={editing}
              numbers={candidates.numbers}
              servers={candidates.servers}
              currentServer={currentServerOption(number, editing)}
              onCancel={() => setEditing(null)}
              onSaved={saved}
            />
          )}

          {assignable && !editing && number.assignments.length === 0 && number.lease && (
            <Button
              size="sm"
              onClick={() => setEditing({ phoneNumber: number.phoneNumber })}
              className="w-full"
            >
              <Plus className="h-3.5 w-3.5" />
              Assign this number to a server
            </Button>
          )}

          {number.assignments.map((a) => (
            <FactSection key={a.serverIp} title={a.server?.name ?? a.serverIp}>
              <Fact label="Server IP" value={a.serverIp} mono />
              {a.server ? (
                <ServerFacts server={a.server} />
              ) : (
                <Fact label="Instance" value="No EC2 instance holds this address" />
              )}
              <Fact label="Registry label" value={a.customer} mono />
              {a.organizationId && (
                <Fact
                  label="X5 organization"
                  value={
                    <Link
                      to={`/organizations/${encodeURIComponent(a.organizationId)}`}
                      className="text-accent hover:underline"
                    >
                      {a.organizationName}
                    </Link>
                  }
                />
              )}
              {assignable && !editing && (
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditing({
                        phoneNumber: number.phoneNumber,
                        serverIp: a.serverIp,
                        replaces: { serverIp: a.serverIp, phoneNumber: number.phoneNumber },
                      })
                    }
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Change
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removing}
                    onClick={() =>
                      void remove(a.serverIp, number.phoneNumber, a.server?.name ?? a.customer)
                    }
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {removing ? 'Removing…' : 'Remove'}
                  </Button>
                </div>
              )}
            </FactSection>
          ))}

          {number.lease ? (
            <FactSection title="Number">
              <Fact label="E.164" value={number.phoneNumber} mono />
              <Fact label="Type" value={number.lease.numberType.replace('_', ' ')} />
              <Fact label="Country" value={number.lease.isoCountryCode} />
              <Fact label="Capabilities" value={number.lease.capabilities.join(', ')} />
              <Fact
                label="Two-way"
                value={
                  <span className="flex flex-wrap items-center justify-end gap-2">
                    {number.lease.twoWayEnabled ? (
                      <span>Enabled</span>
                    ) : (
                      <span className="text-caution">Disabled — inbound replies are dropped</span>
                    )}
                    {canWrite && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={togglingTwoWay}
                        onClick={() => void toggleTwoWay(number)}
                      >
                        {togglingTwoWay
                          ? 'Saving…'
                          : number.lease?.twoWayEnabled
                            ? 'Disable'
                            : 'Enable'}
                      </Button>
                    )}
                  </span>
                }
              />
              <Fact label="Inbound topic" value={number.lease.twoWayChannelArn} mono />
              <Fact label="Opt-out list" value={number.lease.optOutListName} />
              <Fact label="Monthly price" value={formatUsd(number.lease.monthlyPrice)} />
              <Fact label="Leased since" value={formatDateTime(number.lease.createdAt)} />
              <Fact
                label="Deletion protection"
                value={number.lease.deletionProtection ? 'On' : 'Off'}
              />
              <Fact label="Pool" value={number.lease.poolId} mono />
              <Fact label="Registration" value={number.lease.registrationId} mono />
              <Fact label="Phone number id" value={number.lease.phoneNumberId} mono />
            </FactSection>
          ) : (
            <FactSection title="Number">
              <p className="py-1.5 text-sm text-muted-foreground">
                AWS does not lease{' '}
                <span className="font-mono text-foreground">{number.phoneNumber}</span>, so there is
                no lease to show. This server sends from the shared fallback instead.
              </p>
              {number.suggestedNumber && (
                <p className="pb-1.5 text-sm text-caution">
                  Looks like <span className="font-mono">{number.suggestedNumber}</span>, which is
                  leased and unassigned.
                </p>
              )}
            </FactSection>
          )}
        </div>
      )}
    </Drawer>
  );
}

/**
 * The candidate list excludes assigned servers, so a move has to re-offer the
 * one it is already on — built from the row rather than synthesised, so the
 * picker shows the real instance name and id.
 */
function currentServerOption(
  number: SmsNumber,
  target: AssignmentTarget,
): SmsCandidateServer | undefined {
  const assignment = number.assignments.find((a) => a.serverIp === target.serverIp);
  if (!assignment) return undefined;
  return {
    instanceId: assignment.server?.instanceId ?? '',
    name: assignment.server?.name ?? null,
    label: assignment.customer,
    serverIp: assignment.serverIp,
    state: assignment.server?.state ?? 'unknown',
    availabilityZone: assignment.server?.availabilityZone ?? null,
  };
}

function StatusBadge({ status }: { status: SmsNumberStatus }) {
  const label = STATUS_LABEL[status];
  switch (status) {
    case 'duplicate':
    case 'unleased':
    case 'conflicted':
      return <Badge variant="destructive">{label}</Badge>;
    case 'assigned':
      return <Badge variant="success">{label}</Badge>;
    case 'shared':
      return <Badge variant="admin">{label}</Badge>;
    case 'default':
      return <Badge variant="outline">{label}</Badge>;
    default:
      return <Badge variant="secondary">{label}</Badge>;
  }
}

/**
 * The two failure modes read very differently, so they are explained rather than
 * left as badges in a 120-row table. Nothing here writes — the fix is a change
 * to the registry, made deliberately outside the portal.
 */
function ProblemsCard({ problems }: { problems: SmsNumber[] }) {
  const unleased = problems.filter((n) => n.status === 'unleased');
  const duplicates = problems.filter((n) => n.status === 'duplicate');
  const conflicted = problems.filter((n) => n.status === 'conflicted');
  const fallbackAssigned = problems.filter((n) => n.status === 'default');

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          {formatNumber(problems.length)} number{problems.length === 1 ? '' : 's'} need attention
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        {unleased.length > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">Not leased.</span> The registry names a
              number AWS says we do not have, so the gateway falls back to the shared number and
              these customers are not sending from their own.
            </p>
            <ul className="space-y-1.5">
              {unleased.map((n) => (
                <li key={n.phoneNumber} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-foreground">{n.phoneNumber}</span>
                  <span className="text-muted-foreground">
                    {n.assignments.map((a) => `${a.customer} (${a.serverIp})`).join(', ')}
                  </span>
                  {n.suggestedNumber && (
                    <span className="text-caution">
                      → looks like <span className="font-mono">{n.suggestedNumber}</span>, which is
                      leased and unassigned
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {conflicted.length > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">Two numbers, one server.</span> The
              gateway returns the shared fallback whenever it finds more than one number for a
              server, so <span className="text-foreground">none</span> of these is actually in use —
              not even the one that looks right. Remove all but one per server.
            </p>
            <ul className="space-y-1.5">
              {conflicted.map((n) => (
                <li key={n.phoneNumber} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-foreground">{n.phoneNumber}</span>
                  <span className="text-muted-foreground">
                    {n.assignments.map((a) => `${a.server?.name ?? a.customer} (${a.serverIp})`).join(', ')}
                  </span>
                  <span className="text-caution">
                    also holds <span className="font-mono">{n.conflictsWith.join(', ')}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {fallbackAssigned.length > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">The shared fallback is assigned.</span>{' '}
              Every server without a number of its own already sends from it, so giving it to one
              customer makes that customer indistinguishable from all unattributed traffic. It
              should hold no assignment at all.
            </p>
            <ul className="space-y-1.5">
              {fallbackAssigned.map((n) => (
                <li key={n.phoneNumber} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-foreground">{n.phoneNumber}</span>
                  <span className="text-muted-foreground">
                    {n.assignments.map((a) => `${a.customer} (${a.serverIp})`).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">Claimed twice.</span> Two different
              customers send from one number, so an inbound reply cannot be attributed to either.
            </p>
            <ul className="space-y-1.5">
              {duplicates.map((n) => (
                <li key={n.phoneNumber} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-foreground">{n.phoneNumber}</span>
                  <span className="text-muted-foreground">
                    {n.assignments.map((a) => `${a.customer} (${a.serverIp})`).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="truncate text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
